// client/src/hooks/useAIGeracao.js
// Hooks genéricos de geração por IA (Venice.ai), reaproveitados por todo
// botão "Gerar com IA" da plataforma — texto (templates, páginas
// institucionais, conteúdos, FAQ, políticas) e imagem (Serviços, Itens de
// Venda, Recursos, Cursos, Pacotes, Ofertas, Conteúdos da Home). Cada
// endpoint de texto já existe no controller específico do recurso (mesmo
// padrão de templateCertificadoController.gerarComIA) — este hook só evita
// repetir o boilerplate de useMutation em cada tela.
import { useMutation } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

// endpoint: caminho completo a partir de /api/v1, ex:
// '/templates/contrato/gerar-ia', '/admin/institucional/faqs/gerar-ia'.
// timeout alto (acima do timeout do server pra Venice, 120s em texto/imagem
// — ver veniceTextService.js/veniceImageService.js) — geração de conteúdo
// longo (ex: contrato completo) pode legitimamente passar do default global
// de 30s do apiClient; sem isso o axios aborta antes do server responder e
// o usuário vê um erro genérico mesmo quando a IA ainda ia terminar.
const AI_GERACAO_TIMEOUT = 150000;

export const useGerarTextoIA = (endpoint) => {
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.post(endpoint, payload, { timeout: AI_GERACAO_TIMEOUT });
            return data.data;
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao gerar conteúdo com IA.', 'danger');
        },
    });
};

// Converte o base64 devolvido pela geração de imagem num File de verdade —
// dali pra frente é tratado exatamente como um arquivo escolhido
// manualmente no input de upload (mesmo padrão de BannersHomeFormPage.js).
export const base64ToFile = (base64, mimeType, filename) => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], filename, { type: mimeType });
};

// Endpoint genérico único (server/routes/aiImagemRoutes.js) — reaproveitado
// por todos os fluxos de imagem por IA da plataforma.
export const useGerarImagemIA = () => {
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: async ({ prompt, aspectRatio, negativePrompt, itemContexto }) => {
            const { data } = await apiClient.post('/ai/imagem/gerar', { prompt, aspectRatio, negativePrompt, itemContexto }, { timeout: AI_GERACAO_TIMEOUT });
            const ext = data.data.mimeType === 'image/png' ? 'png' : 'jpg';
            const file = base64ToFile(data.data.image, data.data.mimeType, `ia-${Date.now()}.${ext}`);
            return { file, previewUrl: URL.createObjectURL(file) };
        },
        onError: (err) => {
            setAlert(err.response?.data?.error || 'Erro ao gerar imagem com IA.', 'danger');
        },
    });
};
